import React, { useEffect, useState } from "react";
import { Row, Col, Form, Alert } from "react-bootstrap";
import axiosClient from "../../../../config/axios";
import moment from "moment-timezone";

function InfringementData({ values, setFieldValue, errors, disabled }) {
  const [serviceTypes, setServiceTypes] = useState([]);
  const [tiempoTranscurrido, setTiempoTranscurrido] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingFecha, setLoadingFecha] = useState(false);
  const [fechaAutoSugerida, setFechaAutoSugerida] = useState(false);
  const [folioBlocked, setFolioBlocked] = useState(false);
  const [loadingFolio, setLoadingFolio] = useState(false);

  const getLocalDateTimeNow = () => {
    return moment
      .tz("America/Mexico_City")
      .seconds(0)
      .milliseconds(0)
      .format("YYYY-MM-DDTHH:mm");
  };

  useEffect(() => {
    setLoading(true);
    axiosClient
      .get("/typeServices/datatable")
      .then((response) => {
        const options = response.data.data.map((item) => ({
          value: item.id,
          label: item.type_service_name,
        }));
        setServiceTypes(options);
      })
      .catch((error) => {
        console.error("Error al obtener tipos de servicio:", error);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (values.fecha_hora_orden) {
      const fechaOrden = moment.tz(
        values.fecha_hora_orden,
        "America/Mexico_City"
      );
      const ahora = moment.tz("America/Mexico_City");

      const duracion = moment.duration(ahora.diff(fechaOrden));
      const dias = Math.floor(duracion.asDays());
      const horas = duracion.hours();

      setTiempoTranscurrido({ dias, horas });
    } else {
      setTiempoTranscurrido(null);
    }
  }, [values.fecha_hora_orden]);

  useEffect(() => {
    // Solo obtener la última fecha si el campo está vacío
    if (!values.fecha_hora_orden) {
      setLoadingFecha(true);
      axiosClient
        .get(
          "/order-vial/datatable?draw=1&start=0&length=50&search=&sort=id&dir=desc"
        )
        .then((response) => {
          const ordenes = response.data.data || [];
          // Buscar la primera orden que tenga fecha válida (no null ni vacía)
          const ordenConFecha = ordenes.find(
            (orden) =>
              orden.fecha_hora_orden &&
              orden.fecha_hora_orden !== null &&
              orden.fecha_hora_orden !== ""
          );

          if (ordenConFecha?.fecha_hora_orden) {
            const fecha = moment
              .utc(ordenConFecha.fecha_hora_orden)
              .seconds(0)
              .milliseconds(0)
              .format("YYYY-MM-DDTHH:mm");

            setFieldValue("fecha_hora_orden", fecha);
            setFechaAutoSugerida(true);
          }
        })
        .catch((error) => {
          console.error("Error al obtener la última orden:", error);
        })
        .finally(() => setLoadingFecha(false));
    }
  }, []);

  // useEffect para manejar el bloqueo del folio cuando se selecciona "Guardia nacional"
  useEffect(() => {
    // Solo aplicar esta lógica si es una nueva orden (no tiene id)
    const isNewOrder = !values.id;

    if (!isNewOrder) {
      // Si es una orden editada, no bloquear el folio
      setFolioBlocked(false);
      return;
    }

    // Buscar si el tipo de servicio seleccionado es "Guardia nacional"
    const selectedServiceType = serviceTypes.find(
      (type) => type.value === values.tipoServicio
    );

    const isGuardiaNacional =
      selectedServiceType &&
      selectedServiceType.label &&
      selectedServiceType.label.toLowerCase().includes("guardia nacional");

    if (isGuardiaNacional && values.tipoServicio) {
      // Bloquear el folio
      setFolioBlocked(true);

      // Obtener el consecutivo solo si el folio no está establecido o está vacío
      if (!values.folio || values.folio === "") {
        setLoadingFolio(true);
        axiosClient
          .get("/order-vial/next-consecutive-folio")
          .then((response) => {
            const { formattedFolio } = response.data;
            // Establecer el folio con el consecutivo formateado (0001, 0002, etc.)
            setFieldValue("folio", formattedFolio);
          })
          .catch((error) => {
            console.error(
              "Error al obtener el folio consecutivo:",
              error
            );
          })
          .finally(() => {
            setLoadingFolio(false);
          });
      }
    } else {
      // Si no es "Guardia nacional", desbloquear el folio
      setFolioBlocked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.tipoServicio, values.id, serviceTypes.length]);

  return (
    <Row className="mb-3">
      <Col md={6}>
        <Form.Group className="mb-3">
          <Form.Label>Folio</Form.Label>
          {loadingFolio ? (
            <div className="text-center my-2">
              <i className="fa fa-spinner fa-spin"></i>
              <span className="ms-2">Obteniendo folio consecutivo...</span>
            </div>
          ) : (
            <Form.Control
              type="text"
              name="folio"
              placeholder="Folio"
              value={values.folio}
              onChange={(e) => setFieldValue("folio", e.target.value)}
              isInvalid={!!errors.folio}
              disabled={disabled || folioBlocked}
            />
          )}
          {folioBlocked && (
            <Form.Text className="text-muted" style={{ fontSize: "0.875rem" }}>
              <i className="fa fa-lock me-1" aria-hidden="true"></i>
              El folio está bloqueado automáticamente para "Guardia nacional".
            </Form.Text>
          )}
          <Form.Control.Feedback type="invalid">
            {errors.folio}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Número de expediente</Form.Label>
          <Form.Control
            type="text"
            name="expedienteNo"
            placeholder="Número de expediente"
            value={values.expedienteNo}
            onChange={(e) => setFieldValue("expedienteNo", e.target.value)}
            isInvalid={!!errors.expedienteNo}
            disabled={disabled}
          />
          <Form.Control.Feedback type="invalid">
            {errors.expedienteNo}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Número de infracción</Form.Label>
          <Form.Control
            type="text"
            name="infraccionNo"
            placeholder="Número de infracción"
            value={values.infraccionNo}
            onChange={(e) => setFieldValue("infraccionNo", e.target.value)}
            isInvalid={!!errors.infraccionNo}
            disabled={disabled}
          />
          <Form.Control.Feedback type="invalid">
            {errors.infraccionNo}
          </Form.Control.Feedback>
        </Form.Group>
      </Col>

      <Col md={6}>
        <Form.Group className="mb-3">
          <Form.Label>Motivo de la infracción</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            name="motivoInfraccion"
            placeholder="Motivo de la infracción"
            value={values.motivoInfraccion}
            onChange={(e) => setFieldValue("motivoInfraccion", e.target.value)}
            isInvalid={!!errors.motivoInfraccion}
            disabled={disabled}
          />
          <Form.Control.Feedback type="invalid">
            {errors.motivoInfraccion}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Fecha y hora de reporte</Form.Label>
          
          {loadingFecha ? (
            <div className="text-center my-4">
              <i className="fa fa-spinner fa-spin fa-3x"></i>
              <p>Cargando...</p>
            </div>
          ) : (
            <>
              <Form.Control
                type="datetime-local"
                name="fecha_hora_orden"
                value={values.fecha_hora_orden}
                max={getLocalDateTimeNow()}
                onChange={(e) => {
                  setFieldValue("fecha_hora_orden", e.target.value);
                  setFechaAutoSugerida(false); // Resetear indicador cuando el usuario cambia manualmente
                }}
                isInvalid={!!errors.fecha_hora_orden}
                disabled={disabled}
              />
              {fechaAutoSugerida && (
                <Form.Text className="text-muted" style={{ fontSize: "0.875rem" }}>
                  <i className="fa fa-info-circle me-1" aria-hidden="true"></i>
                  Esta fecha es una sugerencia basada en la última orden con fecha válida.
                </Form.Text>
              )}
              <Form.Control.Feedback type="invalid">
                {errors.fecha_hora_orden}
              </Form.Control.Feedback>
            </>
          )}
        </Form.Group>

        {tiempoTranscurrido && (
          <Alert variant={tiempoTranscurrido.dias > 0 ? "warning" : "info"}>
            {tiempoTranscurrido.dias === 0 && tiempoTranscurrido.horas === 0
              ? "La orden fue registrada hace menos de una hora."
              : `Han pasado ${tiempoTranscurrido.dias} día(s) y ${tiempoTranscurrido.horas} hora(s) desde la fecha de la orden.`}
          </Alert>
        )}

        {/* Select de tipo de servicio */}
        <Form.Group className="mb-3">
          <Form.Label>Tipo de servicio</Form.Label>

          {loading ? (
            <div className="text-center my-4">
              <i className="fa fa-spinner fa-spin fa-3x"></i>
              <p>Cargando...</p>
            </div>
          ) : (
            <Form.Select
              name="tipoServicio"
              value={values.tipoServicio}
              onChange={(e) =>
                setFieldValue("tipoServicio", parseInt(e.target.value))
              }
              isInvalid={!!errors.tipoServicio}
              disabled={disabled}
            >
              <option value="">Selecciona una opción</option>
              {serviceTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Form.Select>
          )}

          <Form.Control.Feedback type="invalid">
            {errors.tipoServicio}
          </Form.Control.Feedback>
        </Form.Group>
      </Col>
    </Row>
  );
}

export default InfringementData;
